/**
 * Each report entry's probability, as a 0–1 fraction.
 *
 * `probability` is 0–1 on both the graph path and the LLM fallback, so it wins when stored.
 * Without it, `likelihood` is read instead: 0–1 on the graph path, 0–100 on the LLM fallback
 * and on legacy reports. That scale is decided once for the whole report, so a fallback entry
 * at 0.5% does not print as 50%.
 */

import type { ReportDifferential } from "../../api/types";

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** The entries' probabilities, index for index; `null` where neither field holds a number. */
export function reportProbabilities(entries: readonly ReportDifferential[]): (number | null)[] {
  const percent = entries.some((entry) => finite(entry.likelihood) && entry.likelihood > 1);
  return entries.map((entry) => {
    if (finite(entry.probability)) return entry.probability;
    if (!finite(entry.likelihood)) return null;
    return percent ? entry.likelihood / 100 : entry.likelihood;
  });
}
